import React, { useState, useEffect, useCallback, useMemo, useRef, createContext } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useUnsavedChanges } from '../../../utils/custom-hooks';
import { selectors as groupSelectors } from '../../../state/modules/groups';
import { selectors as userSelectors } from '../../../state/modules/users';
import { selectors as membershipSelectors } from '../../../state/modules/memberships';
import { selectors as skillSelectors } from '../../../state/modules/skills';
import { selectors as availabilityCodeSelectors } from '../../../state/modules/availability-codes';
import { selectors as functionSelectors } from '../../../state/modules/standby-duty-functions';
import { operations as dutyTransactionOperations } from '../../../state/modules/duty-transactions';
import { selectors as absenceRequestSelectors } from '../../../state/modules/absence-requests';
import { selectors as dutyExchangeSelectors } from '../../../state/modules/duty-exchanges';
import {
  registerForCombinedScheduleUpdates,
  updateIntervalsInMemory as updateCombinedScheduleIntervalsInMemory,
  updateIntervalsInBackend as updateCombinedScheduleIntervalsInBackend
} from '../../../state/modules/combined-schedules/operations';
import { error as combinedScheduleErrors } from '../../../state/modules/combined-schedules/selectors';
import { fetchCombinedSchedulesWithSeatAllocations } from '../../../state/modules/computer-aided-scheduling/operations';
import { unsavedChangesPresent, selectLoading } from '../../../state/modules/combined-schedules/selectors';
import { splitCombinedScheduleIntervals } from '../../../state/modules/combined-schedules/actions';
import { dutyPlannerSelectors, dutyPlannerOperations } from '../../../state/modules/duty-planner';
import CandidateList from './CandidateList';
import Loader from '../../general/Loader';
import './DutyPlanner.css';
// make sure you include the timeline stylesheet or the timeline will not be styled
import 'react-calendar-timeline/lib/Timeline.css';
import { Row, Col } from '../../ui-kit';
import ScheduleIntervalContextMenu from './ScheduleIntervalContextMenu';
import CanvasContextMenu from './CanvasContextMenu';

import IntervalCommentsEditModal from './IntervalCommentsEditModal';
import DutyPlannerToolbar from './DutyPlannerToolbar';
import DutyPlannerTimeline from './DutyPlannerTimeline';
import BarLoader from 'react-spinners/BarLoader';
import ScheduleWarningsPopoverConnected from '../warnings/ScheduleWarningsPopoverConnected';
import DashboardAPIManager from '../../dashboards/DashboardAPIManager';

export const DutyPlannerContext = createContext({ stations: [], stationSettings: {}, dutyFunctionsPerStation: [] });

const Popups = ({
  readOnly,
  commentsDialogIntervalId,
  hideCommentsDialog,
  updateIntervalById,
  findItemById,
  intervalContextMenu,
  setIntervalContextMenuShow,
  openCommentsDialog,
  selectableAvailabilityCodes,
  splitCombinedScheduleIntervals,
  removeInterval,
  canvasContextMenu,
  setCanvasContextMenuShow,
  canvasContextMenuPeriodStart,
  canvasContextMenuPeriodEnd,
  groupId,
  setSaving
}) => {
  /* In read-only mode, the user will not interact with the intervals at
          all, we don't need Modals, Context menu or Candidate List */
  if (readOnly) return;

  return (
    <>
      <ScheduleWarningsPopoverConnected
        handleUpdated={() => setSaving(false)}
        handleDismissed={() => setSaving(false)}
      />
      <IntervalCommentsEditModal
        show={!!commentsDialogIntervalId}
        onHide={hideCommentsDialog}
        updateIntervalById={updateIntervalById}
        interval={findItemById(commentsDialogIntervalId)}
      />
      <ScheduleIntervalContextMenu
        className="d-print-none"
        show={intervalContextMenu.show}
        coordX={intervalContextMenu.coordX}
        coordY={intervalContextMenu.coordY}
        setShow={setIntervalContextMenuShow}
        openCommentsDialog={openCommentsDialog}
        interval={findItemById(intervalContextMenu.intervalId)}
        updateIntervalById={updateIntervalById}
        availabilityCodes={selectableAvailabilityCodes}
        time={intervalContextMenu.time}
        splitInterval={splitCombinedScheduleIntervals}
        removeInterval={removeInterval}
      />
      <CanvasContextMenu
        className="d-print-none"
        show={canvasContextMenu.show}
        coordX={canvasContextMenu.coordX}
        coordY={canvasContextMenu.coordY}
        dutyFunctionId={canvasContextMenu.dutyFunctionId}
        setShow={setCanvasContextMenuShow}
        startTime={canvasContextMenuPeriodStart}
        endTime={canvasContextMenuPeriodEnd}
        groupId={groupId}
      />
    </>
  );
};

const DutyPlanner = ({
  // direct props
  startTime,
  endTime,
  groupId,
  readOnly,
  stations,
  stationSettings,
  widgetMode,
  // redux props
  dutyPlanner,
  initialLoad,
  schedulableAvailabilityCodes,
  dutyFunctionsPerStation,
  usersOffDuty,
  usersOnCall,
  assignedUsersTimelineItems,
  dutyRequestTimelineItems,
  timelineGroups,
  hasUnsavedChanges,
  combinedScheduleLoading,
  combinedScheduleErrors,
  // dispatch
  setDutyPlannerData,
  updateCombinedScheduleIntervalsInMemory,
  updateCombinedScheduleIntervalsInBackend,
  registerForCombinedScheduleUpdates,
  fetchCombinedSchedulesWithSeatAllocations,
  splitCombinedScheduleIntervals,
  fetchDutyTransactions,
  dashboardAPIManager
}) => {
  dashboardAPIManager ||= new DashboardAPIManager();
  useEffect(() => {
    dashboardAPIManager.setDefaultGroupId(groupId);
    dashboardAPIManager.fetchGroupAndSubgroups();
    dashboardAPIManager.fetchUsers();
    dashboardAPIManager.fetchMemberships();
    dashboardAPIManager.fetchSkills();
    dashboardAPIManager.fetchAvailabilityCodes();
    dashboardAPIManager.fetchStandbyDutyFunctions();
  }, [groupId, dashboardAPIManager]);

  useEffect(() => {
    if (combinedScheduleErrors) setUpdating(false);
  }, [combinedScheduleErrors]);

  // any time props change, sync this with the duty planner redux
  useEffect(() => {
    setDutyPlannerData(startTime, endTime, groupId, readOnly);
  }, [startTime, endTime, groupId, readOnly, setDutyPlannerData]);

  const [periodSelectorTimes, setPeriodSelectorTimes] = useState({
    startTime,
    endTime
  });
  const [commentsDialogIntervalId, setCommentsDialogIntervalId] = useState(null);

  // context menu when right clicking on an interval
  const [intervalContextMenu, setIntervalContextMenu] = useState({
    show: false,
    coordX: null,
    coordY: null,
    time: null
  });
  // context menu when right clicking on the canvas (no interval present)
  const [canvasContextMenu, setCanvasContextMenu] = useState({
    show: false,
    coordX: null,
    coordY: null,
    dutyFunctionId: null,
    time: null
  });

  // If any of the stations has a default function set up, we will default to 'duty' mode
  // otherwise we default to 'on_call' mode
  const [plannerMode, setPlannerMode] = useState('on_call');

  // This is used to display a loader when the schedules are being saved
  // saving is usually very fast, nonetheless it is good to have some feedback
  const [isSaving, setSaving] = useState(false);
  const [isUpdating, setUpdating] = useState(false);

  // This calculates the start and end time that should be used when creating a
  // new interval from the canvas context menu. It looks at the intervals that
  // are already present for the duty function, and finds the gap in which the
  // new interval would fit based on the right click time.
  const [canvasContextMenuPeriodStart, canvasContextMenuPeriodEnd] = useMemo(() => {
    const contextMenuIntervals = assignedUsersTimelineItems.filter(i => i.group === canvasContextMenu.dutyFunctionId);

    const gapStartTime = Math.max(
      moment(dutyPlanner.startTime),
      ...contextMenuIntervals.map(i => i.end_time).filter(t => moment(t).isBefore(canvasContextMenu.time))
    );
    const gapEndTime = Math.min(
      moment(dutyPlanner.endTime),
      ...contextMenuIntervals.map(i => i.start_time).filter(t => moment(t).isAfter(canvasContextMenu.time))
    );
    return [gapStartTime, gapEndTime];
  }, [canvasContextMenu, assignedUsersTimelineItems, dutyPlanner.startTime, dutyPlanner.endTime]);

  useUnsavedChanges(hasUnsavedChanges);

  const assign = useCallback(
    ({ functionIds, defaultFunctionId, stationId, user, clearGaps }) => {
      let assigned_function_ids = Array.isArray(functionIds) ? functionIds : [functionIds].filter(Boolean);
      if (assigned_function_ids.length === 0) {
        // If no function is assigned, we will assign the default function
        assigned_function_ids = [defaultFunctionId];
      }

      const intervalModifications = user.intervals.map(interval => ({
        ...interval,
        start_time: interval.start_time,
        end_time: interval.end_time,
        assigned_function_ids,
        station_id: stationId
      }));

      // Apply the update to redux without storing in backend
      updateCombinedScheduleIntervalsInMemory(
        user.membershipId,
        'membership',
        intervalModifications,
        null,
        null,
        clearGaps
      );
    },
    [updateCombinedScheduleIntervalsInMemory, stationSettings]
  );

  const findItemById = itemId => assignedUsersTimelineItems.find(i => i.id === itemId);

  // reference to the current websocket subscription
  const subscriptionRef = useRef(null);

  useEffect(() => {
    const newPlannerMode = Object.values(stationSettings).some(ss => !!ss.defaultFunctionId) ? 'duty' : 'on_call';
    setPlannerMode(newPlannerMode);
  }, [stationSettings]);

  useEffect(() => {
    // Whenever the duty planner period changes, we fetch new schedules if
    // necessary
    // NOTE: If the Duty planner is a widget, It should react to any change in
    // the dashboard datetime, which is passed as a prop from the widget
    // wrapper. This is achieved through the useEffect which synchronizes
    // these props with the duty planner Redux values
    const { groupId, startTime, endTime } = dutyPlanner;
    if (!startTime || !endTime) return; // happens on boot when undefined

    const lookAhead = moment(endTime).diff(startTime, 'seconds');

    // Cancel old subscription if exists
    if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
    // Fetch the new schedules and register to websockets for real time
    // updates
    subscriptionRef.current = registerForCombinedScheduleUpdates({
      groupIds: [groupId],
      type: 'membership',
      look_ahead: lookAhead,
      start_time: startTime
    });

    // Fetch the new absence requests
    fetchDutyTransactions({
      startTime,
      endTime,
      forceFetch: true,
      params: { group_ids: groupId }
    });
  }, [dutyPlanner.startTime, dutyPlanner.endTime]);

  const updateIntervalById = useCallback(
    (intervalId, newAttributes) => {
      const item = findItemById(intervalId);
      if (!item) return;
      storeInterval({ ...item, ...newAttributes });
    },
    [assignedUsersTimelineItems]
  );

  // this method is called when an interval is manipulated. It updates the
  // updated period in the in-memory store, which optimistically updates redux,
  // so the timeline should be immediately updated through the
  // assignedUsersTimelineItems selector
  const storeInterval = useCallback((interval, updateStart = null, updateEnd = null) => {
    const membershipId = interval.membership_id;
    updateStart = updateStart || interval.start_time;
    updateEnd = updateEnd || interval.end_time;

    // Apply the update to redux without storing in backend
    updateCombinedScheduleIntervalsInMemory(membershipId, 'membership', [interval], updateStart, updateEnd);
  }, []);

  // This method will change the value of the Period Selector.
  const periodChange = useCallback(values => {
    setPeriodSelectorTimes({
      startTime: moment(values.start_time).format(),
      endTime: moment(values.end_time).format()
    });
  }, []);

  // This method will sync the local state with redux. We do this to prevent
  // a re-fetch of data for every small change to any of the period selector values.
  // Changes to redux will only be triggered when clicking the Apply button.
  const syncPeriodWithDutyPlannerRedux = useCallback(() => {
    if (hasUnsavedChanges) {
      globalThis.alert(I18n.t('save_states.unsaved_changes_warning'));
      return;
    }

    setDutyPlannerData(periodSelectorTimes.startTime, periodSelectorTimes.endTime);
  }, [hasUnsavedChanges, periodSelectorTimes.startTime, periodSelectorTimes.endTime]);

  // This function moves the duty planner period by one day. This works under the
  // assumption that Groninngen shifts are 24h. This can be later adapted for different periods
  const changeViewingPeriod = days => {
    if (hasUnsavedChanges) {
      globalThis.alert(I18n.t('save_states.unsaved_changes_warning'));
      return;
    }

    let newStartTime = moment(dutyPlanner.startTime);
    let newEndTime = moment(dutyPlanner.endTime);
    if (days === 'today') {
      days = moment().startOf('day').diff(newStartTime.clone().startOf('day'), 'days');
      // reset the length of the period to 24h.
      newEndTime = newStartTime.clone().add(1, 'day');
    }

    newStartTime = newStartTime.clone().add(days, 'day').format();
    newEndTime = newEndTime.clone().add(days, 'day').format();

    // Update the values of the period selector as well
    setPeriodSelectorTimes({
      startTime: newStartTime,
      endTime: newEndTime
    });

    setDutyPlannerData(newStartTime, newEndTime);
  };

  const setIntervalContextMenuShow = useCallback(
    (show, coordX, coordY, intervalId, time) => {
      // avoid unnecessary updates
      if (!show && !intervalContextMenu.show) return;
      // close the other context menu when one is opened
      if (show) setCanvasContextMenuShow(false);
      setIntervalContextMenu({ show, coordX, coordY, intervalId, time });
    },
    [intervalContextMenu.show, setCanvasContextMenuShow]
  );

  const setCanvasContextMenuShow = useCallback(
    (show, coordX, coordY, dutyFunctionId, time) => {
      // avoid unnecessary updates
      if (!show && !canvasContextMenu.show) return;
      // close the other context menu when one is opened
      if (show) setIntervalContextMenuShow(false);
      setCanvasContextMenu({ show, coordX, coordY, dutyFunctionId, time });
    },
    [canvasContextMenu.show, setIntervalContextMenu]
  );

  const openCommentsDialog = useCallback(() => {
    // the context dialog is opened from the context menu. We copy the
    // intervalId to the commentsDialogIntervalId to open it, and pass a
    // reference to the modal.
    setCommentsDialogIntervalId(intervalContextMenu.intervalId);
    setIntervalContextMenuShow(false);
    setCanvasContextMenuShow(false);
  }, [intervalContextMenu.intervalId, setIntervalContextMenuShow, setCanvasContextMenuShow]);

  const hideCommentsDialog = useCallback(() => setCommentsDialogIntervalId(null), []);

  // Function that saves all of the changes one by one to the database.
  // Returns a promise so the change clearance can happen once with is
  // done
  const processUnsavedChanges = async ({ ignoreWarnings }) => {
    setSaving(true);

    try {
      await updateCombinedScheduleIntervalsInBackend({ ignoreWarnings });
    } finally {
      setSaving(false);
    }
  };

  const processUnsavedChangesRespectWarnings = useCallback(() => processUnsavedChanges({ ignoreWarnings: false }), []);

  // These are the availability codes which can be selected in the context menu.
  // Only those that are associated to the station.
  const selectableAvailabilityCodes = useMemo(
    () => schedulableAvailabilityCodes.filter(c => (c.applicable_to_groups || []).includes(groupId)),
    [schedulableAvailabilityCodes, groupId]
  );

  const removeInterval = useCallback(interval => {
    const emptyInterval = {
      ...interval,
      // Remove all assigned functions to clear the shift
      assigned_function_ids: []
    };

    updateCombinedScheduleIntervalsInMemory(
      interval.membership_id,
      'membership',
      [emptyInterval],
      interval.start_time,
      interval.end_time
    );
  }, []);

  // Functions that returns the Combined schedule intervals suggested
  const getAutoSeatAllocations = useCallback(async () => {
    if (hasUnsavedChanges) {
      globalThis.alert(I18n.t('save_states.unsaved_changes_warning'));
      return;
    }

    const { groupId, startTime, endTime } = dutyPlanner;

    setUpdating(true);

    await fetchCombinedSchedulesWithSeatAllocations({
      group_id: groupId,
      start_time: startTime,
      end_time: endTime,
      mode: plannerMode
    });

    setUpdating(false);
  }, [dutyPlanner, hasUnsavedChanges, plannerMode]);

  /*
      DUTY PLANNER LAYOUT
      -----------------------------------------------------
      Toolbar (date selector and navigators)
      -----------------------------------------------------
      Candidate | *Timeline*
      List      |
  */

  if (!initialLoad) return <Loader />;

  const isProcessing = isUpdating || isSaving;
  const isLoading = combinedScheduleLoading;

  // All the items that contain className={'d-print-none'}
  // will be ignored when printing.
  return (
    <DutyPlannerContext.Provider value={{ stations, stationSettings, dutyFunctionsPerStation }}>
      <>
        <Popups
          readOnly={readOnly}
          commentsDialogIntervalId={commentsDialogIntervalId}
          hideCommentsDialog={hideCommentsDialog}
          updateIntervalById={updateIntervalById}
          findItemById={findItemById}
          intervalContextMenu={intervalContextMenu}
          setIntervalContextMenuShow={setIntervalContextMenuShow}
          openCommentsDialog={openCommentsDialog}
          selectableAvailabilityCodes={selectableAvailabilityCodes}
          splitCombinedScheduleIntervals={splitCombinedScheduleIntervals}
          removeInterval={removeInterval}
          canvasContextMenu={canvasContextMenu}
          setCanvasContextMenuShow={setCanvasContextMenuShow}
          canvasContextMenuPeriodStart={canvasContextMenuPeriodStart}
          canvasContextMenuPeriodEnd={canvasContextMenuPeriodEnd}
          groupId={groupId}
          setSaving={setSaving}
        />
        <Row className="position-relative" style={{ minHeight: '90vh' }}>
          {!readOnly && (
            <Col lg={2} className="duty-planner sidebar-lg-absolute d-flex flex-column overflow-auto d-print-none">
              <CandidateList
                usersOffDuty={usersOffDuty}
                usersOnCall={usersOnCall}
                setPlannerMode={setPlannerMode}
                plannerMode={plannerMode}
                assign={assign}
                periodStartTime={dutyPlanner.startTime}
                periodEndTime={dutyPlanner.endTime}
              />
            </Col>
          )}

          <Col lg={readOnly ? 12 : 10} className={readOnly ? '' : 'duty-planner content-lg-offset'}>
            {!widgetMode && (
              <DutyPlannerToolbar
                startTime={periodSelectorTimes.startTime}
                endTime={periodSelectorTimes.endTime}
                groupId={groupId}
                changeViewingPeriod={changeViewingPeriod}
                periodChange={periodChange}
                syncPeriodWithDutyPlannerRedux={syncPeriodWithDutyPlannerRedux}
                storeChanges={processUnsavedChangesRespectWarnings}
                hasUnsavedChanges={hasUnsavedChanges}
                isSaving={isSaving}
                isProcessing={isProcessing}
                readOnly={readOnly}
                getAutoSeatAllocations={getAutoSeatAllocations}
                isLoading={isLoading}
              />
            )}
            {(isProcessing || isLoading) && <BarLoader height="4px" width="100%" color="#c30000" />}
            {!isProcessing && !isLoading && <div style={{ height: '4px' }} />}
            <DutyPlannerTimeline
              startTime={dutyPlanner.startTime}
              endTime={dutyPlanner.endTime}
              setIntervalContextMenuShow={setIntervalContextMenuShow}
              setCanvasContextMenuShow={setCanvasContextMenuShow}
              storeInterval={storeInterval}
              readOnly={readOnly}
              timelineGroups={timelineGroups}
              plannerMode={plannerMode}
              assignedUsersTimelineItems={assignedUsersTimelineItems}
              dutyRequestTimelineItems={dutyRequestTimelineItems}
            />
          </Col>
        </Row>
      </>
    </DutyPlannerContext.Provider>
  );
};

const mapStateToProps = state => ({
  dutyPlanner: state.dutyPlanner,
  initialLoad:
    groupSelectors.initialLoadComplete(state) &&
    userSelectors.initialLoadComplete(state) &&
    membershipSelectors.initialLoadComplete(state) &&
    skillSelectors.initialLoadComplete(state) &&
    availabilityCodeSelectors.initialLoadComplete(state) &&
    absenceRequestSelectors.initialLoadComplete(state) &&
    dutyExchangeSelectors.initialLoadComplete(state) &&
    functionSelectors.initialLoadComplete(state),
  schedulableAvailabilityCodes: availabilityCodeSelectors.selectSchedulableAvailabilityCodes(state),
  stations: dutyPlannerSelectors.dutyPlannerStations(state),
  stationSettings: dutyPlannerSelectors.stationSettings(state),
  dutyFunctionsPerStation: dutyPlannerSelectors.dutyFunctionsPerStation(state),
  usersOffDuty: dutyPlannerSelectors.usersOffDuty(state),
  usersOnCall: dutyPlannerSelectors.usersOnCall(state),
  assignedUsersTimelineItems: dutyPlannerSelectors.assignedUsersTimelineItems(state),
  dutyRequestTimelineItems: dutyPlannerSelectors.dutyRequestTimelineItems(state),
  timelineGroups: dutyPlannerSelectors.timelineGroupsAugmented(state),
  hasUnsavedChanges: unsavedChangesPresent(state),
  combinedScheduleLoading: selectLoading(state),
  combinedScheduleErrors: combinedScheduleErrors(state)
});

const mapDispatchToProps = {
  setDutyPlannerData: dutyPlannerOperations.setDutyPlannerData,
  updateCombinedScheduleIntervalsInMemory: updateCombinedScheduleIntervalsInMemory,
  updateCombinedScheduleIntervalsInBackend: updateCombinedScheduleIntervalsInBackend,
  registerForCombinedScheduleUpdates,
  fetchCombinedSchedulesWithSeatAllocations: fetchCombinedSchedulesWithSeatAllocations,
  splitCombinedScheduleIntervals,
  fetchDutyTransactions: dutyTransactionOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(DutyPlanner);
